import {
  Component,
  forwardRef,
  Input,
  OnInit,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { FormulaValidatorService } from '../../services/formula-validator.service';
import { isDirectCondition } from '../../utils/wxrl-direct';

/**
 * Componente editor de fórmulas lógicas que implementa ControlValueAccessor.
 *
 * Permite editar fórmulas lógicas con:
 * - Selector de condiciones para insertar en la fórmula
 * - Campo de texto editable (máx 255 caracteres)
 * - Botones para operadores lógicos (AND, OR, paréntesis)
 * - Resumen de condiciones disponibles
 * - Validación sintáctica y semántica en tiempo real
 *
 * @component
 * @selector afoone-wxrl-formula-editor
 */
@Component({
  selector: 'afoone-wxrl-formula-editor',
  templateUrl: './wxrl-formula-editor.component.html',
  styleUrls: ['./wxrl-formula-editor.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WxrlFormulaEditorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => WxrlFormulaEditorComponent),
      multi: true,
    },
  ],
})
export class WxrlFormulaEditorComponent
  implements ControlValueAccessor, Validator, OnInit, OnChanges
{
  @Input() conditions: Array<{ condRule: string; kommentar: string }> = [];

  /**
   * Si true, no exige que los identificadores de la fórmula existan en `conditions` (solo sintaxis).
   * Cabecera antes de tener condiciones en el árbol: el usuario puede redactar la fórmula y guardar.
   */
  @Input() allowUnknownConditionIds = false;

  /**
   * Si true, no se abre el diálogo (p. ej. ficha regla sin modo edición — #540).
   * Independiente de setDisabledState del CVA.
   */
  @Input() interactionLocked = false;

  /** CVA deshabilitado */
  private cvaDisabled = false;

  // Valor interno del formulario
  private _value: string = '';
  public displayValue: string = ''; // Valor mostrado en el input readonly
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  // Estado del diálogo
  public dialogVisible: boolean = false;
  public formulaText: string = '';
  public formulaControl: FormControl;
  public validationError: string = '';
  public isValid: boolean = true;

  // Opciones para el selector de condiciones
  public conditionOptions: Array<{ label: string; value: string }> = [];

  /** Evita revalidar en bucle cuando el padre pasa `conditions` con nueva referencia cada CD (#698). */
  private lastConditionsFingerprint: string | undefined;

  constructor(
    private _formulaValidator: FormulaValidatorService,
    private _cdr: ChangeDetectorRef,
    private _translate: TranslateService
  ) {
    this.formulaControl = new FormControl('', [
      Validators.required,
      Validators.maxLength(255),
    ]);
  }

  ngOnInit(): void {
    this.updateConditionOptions();

    // Validar mientras se escribe
    this.formulaControl.valueChanges.subscribe((value) => {
      this.formulaText = value || '';
      this.validateFormula(value);
      this._cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !changes['conditions'] &&
      !changes['interactionLocked'] &&
      !changes['allowUnknownConditionIds']
    ) {
      return;
    }

    this.updateConditionOptions();

    let shouldValidateFormula = !!changes['interactionLocked'] || !!changes['allowUnknownConditionIds'];
    if (changes['conditions']) {
      const fp = (this.conditions ?? [])
        .map((c) => `${String(c.condRule ?? '')}\u0001${String(c.kommentar ?? '')}`)
        .join('|');
      if (fp !== this.lastConditionsFingerprint) {
        this.lastConditionsFingerprint = fp;
        shouldValidateFormula = true;
      }
    }

    if (shouldValidateFormula) {
      this.validateFormula(this.formulaControl?.value ?? this._value ?? '');
    }
    this._cdr.markForCheck();
  }

  /**
   * Actualiza las opciones del selector de condiciones basándose en las condiciones disponibles.
   */
  private updateConditionOptions(): void {
    this.conditionOptions = this.conditions.map((cond) => ({
      label: `${cond.condRule} - ${cond.kommentar}`,
      value: cond.condRule,
    }));
  }

  /**
   * Valida la fórmula sintáctica y semánticamente.
   *
   * @param {string} formula - La fórmula a validar
   */
  private validateFormula(formula: string): void {
    if (!formula || formula.trim().length === 0 || isDirectCondition(formula)) {
      this.isValid = true;
      this.validationError = '';
      this._cdr.markForCheck();
      this.onValidatorChange();
      return;
    }

    // Validación sintáctica
    const syntaxResult = this._formulaValidator.validateSyntax(formula);
    if (!syntaxResult.valid) {
      this.isValid = false;
      this.validationError = syntaxResult.error || 'Error de sintaxis';
      this._cdr.markForCheck();
      this.onValidatorChange();
      return;
    }

    if (this.allowUnknownConditionIds) {
      this.isValid = true;
      this.validationError = '';
      this._cdr.markForCheck();
      this.onValidatorChange();
      return;
    }

    // Validación semántica (verificar que las condiciones existan)
    const conditionIds = this.conditions.map((c) => c.condRule);
    const semanticsResult = this._formulaValidator.validateSemantics(
      formula,
      conditionIds
    );
    if (!semanticsResult.valid) {
      this.isValid = false;
      this.validationError = semanticsResult.error || 'Error semántico';
      this._cdr.markForCheck();
      this.onValidatorChange();
      return;
    }

    this.isValid = true;
    this.validationError = '';
    this._cdr.markForCheck();
    this.onValidatorChange();
  }

  /** No abrir editor si la interacción está bloqueada (#540 cabecera solo lectura). */
  get editorInteractionBlocked(): boolean {
    return this.interactionLocked || this.cvaDisabled;
  }

  /**
   * Abre el diálogo del editor de fórmulas.
   */
  public openEditor(): void {
    if (this.editorInteractionBlocked) {
      return;
    }
    this.formulaText = this._value || '';
    this.formulaControl.setValue(this.formulaText, { emitEvent: false });
    this.validateFormula(this.formulaText);
    this.dialogVisible = true;
    // Diferir la detección de cambios para evitar ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this._cdr.markForCheck();
    }, 0);
  }

  /**
   * Cierra el diálogo del editor de fórmulas.
   */
  public closeEditor(): void {
    this.dialogVisible = false;
    this.onTouched();
  }

  /**
   * Guarda la fórmula y cierra el diálogo.
   */
  public saveFormula(): void {
    if (this.formulaControl.valid && this.isValid) {
      this._value = this.formulaText;
      this.displayValue = this._value;
      this.onChange(this._value);
      this.closeEditor();
      this._cdr.markForCheck();
    }
  }

  /**
   * Inserta una condición en la posición del cursor (igual que los operadores).
   * Añade espacios alrededor de la condición para separarla del resto del texto.
   *
   * @param {string} conditionId - ID de la condición a insertar (ej: "Cond1", "C1")
   * @param {Event} event - Evento del click (opcional)
   */
  public insertCondition(conditionId: string, event?: Event): void {

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!conditionId) {
      console.log('[FormulaEditor] No conditionId provided');
      return;
    }

    // Asegurar que el textarea tenga el foco
    const textarea = document.getElementById('formula-textarea') as HTMLTextAreaElement;
    if (!textarea) {
      console.error('[FormulaEditor] Textarea not found!');
      return;
    }

    textarea.focus();

    const currentValue = this.formulaControl.value || '';
    const cursorPosition = this.getCursorPosition();

    const beforeCursor = currentValue.substring(0, cursorPosition);
    const afterCursor = currentValue.substring(cursorPosition);

    // Determinar si necesitamos espacios antes y después
    const needsSpaceBefore = beforeCursor.length > 0 && !beforeCursor.endsWith(' ') && !beforeCursor.endsWith('(');
    const needsSpaceAfter = afterCursor.length > 0 && !afterCursor.startsWith(' ') && !afterCursor.startsWith(')');

    // Construir el texto a insertar con espacios si es necesario
    let insertText = conditionId;
    if (needsSpaceBefore) {
      insertText = ' ' + insertText;
    }
    if (needsSpaceAfter) {
      insertText = insertText + ' ';
    }

    const newValue = beforeCursor + insertText + afterCursor;

    // Actualizar el formulario y el texto
    this.formulaControl.setValue(newValue);
    this.formulaText = newValue;

    // Validar la nueva fórmula
    this.validateFormula(newValue);

    // Restaurar posición del cursor después de la inserción
    setTimeout(() => {
      if (textarea) {
        const newPosition = cursorPosition + insertText.length;
        textarea.setSelectionRange(newPosition, newPosition);
        textarea.focus();
      }
      this._cdr.markForCheck();
    }, 0);
  }

  /**
   * Inserta un operador lógico en la posición del cursor.
   *
   * @param {string} operator - Operador a insertar (AND, OR, (, ))
   */
  public insertOperator(operator: string): void {
    const currentValue = this.formulaControl.value || '';
    const cursorPosition = this.getCursorPosition();
    const beforeCursor = currentValue.substring(0, cursorPosition);
    const afterCursor = currentValue.substring(cursorPosition);

    // Insertar el operador con espacios si es AND u OR
    let insertText = operator;
    if (operator === 'AND' || operator === 'OR') {
      insertText = ` ${operator} `;
    }

    const newValue = beforeCursor + insertText + afterCursor;
    this.formulaControl.setValue(newValue);
    this.formulaText = newValue;

    // Restaurar posición del cursor
    setTimeout(() => {
      this.setCursorPosition(cursorPosition + insertText.length);
    }, 0);
  }

  /**
   * Obtiene la posición actual del cursor en el campo de texto.
   *
   * @private
   * @returns {number} Posición del cursor
   */
  private getCursorPosition(): number {
    const textarea = document.getElementById('formula-textarea') as HTMLTextAreaElement;
    return textarea ? textarea.selectionStart : 0;
  }

  /**
   * Establece la posición del cursor en el campo de texto.
   *
   * @private
   * @param {number} position - Posición donde colocar el cursor
   */
  private setCursorPosition(position: number): void {
    const textarea = document.getElementById('formula-textarea') as HTMLTextAreaElement;
    if (textarea) {
      textarea.setSelectionRange(position, position);
      textarea.focus();
    }
  }

  /**
   * Obtiene el resumen de condiciones disponibles.
   *
   * @returns {string} Texto con el resumen de condiciones
   */
  public getConditionsSummary(): string {
    if (this.conditions.length === 0) {
      return 'No hay condiciones definidas';
    }

    return this.conditions
      .map((cond) => `${cond.condRule}: ${cond.kommentar}`)
      .join('\n');
  }

  /**
   * TrackBy function para los botones de condiciones.
   *
   * @param {number} index - Índice del elemento
   * @param {any} item - Elemento de la lista
   * @returns {string} Identificador único
   */
  public trackByCondition(index: number, item: any): string {
    return item.value || index.toString();
  }

  // ControlValueAccessor Implementation

  writeValue(value: string): void {
    const normalized = isDirectCondition(value) ? '' : value || '';
    this._value = normalized;
    this.displayValue = normalized;
    this.formulaText = normalized;
    if (this.formulaControl) {
      this.formulaControl.setValue(this._value, { emitEvent: false });
    }
    // Diferir la detección de cambios para evitar ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this._cdr.markForCheck();
    }, 0);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled = isDisabled;
    if (isDisabled) {
      this.formulaControl.disable();
    } else {
      this.formulaControl.enable();
    }
    this._cdr.markForCheck();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const value = (control.value ?? this._value ?? '').toString();
    if (
      !value ||
      value.trim().length === 0 ||
      isDirectCondition(value)
    ) {
      return null;
    }
    return this.isValid ? null : { formulaInvalid: true };
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }
}

